import { flatMap, clamp, compact, compose, get, map } from 'lodash/fp';

import { mapErrorMessages } from '../utils/challengeUtils';
import createReducer from '../utils/createReducer';
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  INCREMENT_PENDING_ENTRY,
  DECREMENT_PENDING_ENTRY,
  SUBMIT_MULTIPLE_SUCCESS,
  SUBMIT_MULTIPLE_REQUEST,
  SUBMIT_MULTIPLE_FAILURE,
  RESET_COMPATIBLE_CHALLENGES,
  TOGGLE_SHOW_CUSTOM,
  TOGGLE_SHOW_H2H
} from '../constants/actionTypes';
import { COMPATIBLE_CHALLENGES } from '../constants/apiTypes';
import decodeChallenge from '../normalizers/decodeChallenge';
import decodeLineup from '../normalizers/decodeLineup';

const initialState = {
  challengeCount: 0,
  challenges: [],
  errorMessages: [],
  isEnteringChallenges: false,
  isLoading: true,
  isRacing: false,
  successfulEntryIds: [],
  pendingEntries: {},
  lineup: {},
  submissionComplete: false,
  showCustom: false,
  showH2H: false
};

const compatibleChallengesReducer = createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => {
    if (action.api === COMPATIBLE_CHALLENGES) {
      return {
        isLoading: true
      };
    }
    return null;
  },
  [API_CALL_SUCCESS]: (state, action) => {
    switch (action.api) {
      case COMPATIBLE_CHALLENGES:
        return {
          isLoading: false,
          challenges: (action.data || []).map(x => decodeChallenge(x, action.meta)),
          pendingEntries: action.data.reduce(
            (memo, challenge) => ({
              ...memo,
              [challenge.id]: 0
            }),
            {}
          ),
          challengeCount: action.meta.challenge_count,
          isRacing: !!get('slate.is_leg', action.meta.lineup),
          lineup: decodeLineup(action.meta.lineup)
        };
      default:
        return null;
    }
  },
  [API_CALL_FAILURE]: (state, action) => {
    if (action.api === COMPATIBLE_CHALLENGES) {
      return {
        isLoading: false
      };
    }
    return null;
  },
  [SUBMIT_MULTIPLE_REQUEST]: () => ({
    isEnteringChallenges: true,
    successfulEntryIds: [],
    submissionComplete: false
  }),
  [SUBMIT_MULTIPLE_SUCCESS]: (state, action) => ({
    isEnteringChallenges: false,
    successfulEntryIds: flatMap(res => map('id', res.data))(action.payload),
    submissionComplete: true,
    errorMessages:
      compose(
        compact,
        flatMap(data => mapErrorMessages(data.errors))
      )(action.payload) || []
  }),
  [SUBMIT_MULTIPLE_FAILURE]: (state, action) => ({
    isEnteringChallenges: false,
    submissionComplete: true,
    errorMessages: mapErrorMessages(action.payload.errors)
  }),
  [RESET_COMPATIBLE_CHALLENGES]: () => initialState,
  [INCREMENT_PENDING_ENTRY]: (state, action) => {
    const challenge = state.challenges.find(c => c.id === action.challengeId);
    const max = challenge.maxEntriesPerPerson;
    return {
      pendingEntries: {
        ...state.pendingEntries,
        [action.challengeId]: clamp(state.pendingEntries[action.challengeId] + 1, 0, max)
      }
    };
  },
  [DECREMENT_PENDING_ENTRY]: (state, action) => ({
    pendingEntries: {
      ...state.pendingEntries,
      [action.challengeId]: state.pendingEntries[action.challengeId] - 1
    }
  }),
  [TOGGLE_SHOW_CUSTOM]: state => ({
    showCustom: !state.showCustom
  }),
  [TOGGLE_SHOW_H2H]: state => ({
    showH2H: !state.showH2H
  })
});

export default compatibleChallengesReducer;
